import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList, Cell,
  Tooltip as RechartTooltip,
} from 'recharts';
import { PageHeader, StatCard, TierBadge, SubTabBar, CHART_COLORS, fmtDollar, fmtPct, fmtCount } from './shared';
import { apiFetch } from '../api';

// Gradient helpers for heatmap — interpolates #cfe2ff (low) → #003c71 (high)
function heatColor(val, min, max) {
  const t = (val - min) / (max - min);
  const r = Math.round(207 * (1 - t));
  const g = Math.round(226 - 166 * t);
  const b = Math.round(255 - 142 * t);
  return { bg: `rgb(${r},${g},${b})`, color: t > 0.55 ? '#ffffff' : '#003c71' };
}

// Format heatmap cell: compact K notation e.g. $2.8K, $22K
function fmtDollarK(v) {
  const s = v < 0 ? '-$' : '$';
  const k = Math.abs(v) / 1000;
  return s + (k % 1 === 0 ? k : k.toFixed(1)) + 'K';
}

// Format util/cost table cell: PMPM and Cost rows as dollar, others as count
function fmtRowVal(val, metric) {
  if (typeof val !== 'number') return val;
  if (metric.includes('PMPM') || metric.includes('Cost')) return fmtDollar(val);
  return fmtCount(val);
}

// Colors for the single O/E stacked bar
const OE_COLORS = ['#003c71', '#0067a0', '#00859e', '#00a7b5', '#5bc4cf', '#9ddde7'];

function LoadingRow() {
  return <div className="p-4 text-muted">Loading…</div>;
}

function ErrorRow() {
  return <div className="p-4 text-danger">Failed to load dashboard data.</div>;
}

// ── Views ─────────────────────────────────────────────────────────────────────

function OverviewView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/overview')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Cost Intelligence  ·  Executive Overview" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-5">
          <div className="card">
            <div className="card-header fw-semibold">Executive Summary Roll-Up</div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 dash-table">
                <thead><tr><th>Metric</th><th>Prior (2024)</th><th>Current (2025)</th><th>Δ ($)</th><th>% Trend</th></tr></thead>
                <tbody>
                  {data.execSummary.map(r => (
                    <tr key={r.metric}>
                      <td className="fw-semibold">{r.metric}</td>
                      <td>{fmtRowVal(r.prior, r.metric)}</td><td>{fmtRowVal(r.current, r.metric)}</td>
                      <td className="fw-semibold">{fmtRowVal(r.current - r.prior, r.metric)}</td>
                      <td className="text-danger fw-semibold">{fmtPct(r.pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-7">
          <div className="card">
            <div className="card-header fw-semibold">Spend Decomposition by Category (PMPM)</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.spendByCategory} margin={{ top: 22, right: 15, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                  <RechartTooltip formatter={v => [`$${v}`, 'PMPM']} />
                  <Bar dataKey="spend" radius={[3, 3, 0, 0]}>
                    {data.spendByCategory.map((_, i) => <Cell key={i} fill={OE_COLORS[i]} />)}
                    <LabelList dataKey="spend" position="top" formatter={v => `$${v}`} style={{ fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header fw-semibold">Cost Driver Specialties — Price, Volume & Acuity Breakdown</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr><th>Specialty</th><th>PMPM</th><th>vs. Benchmark</th><th>Price</th><th>Volume</th><th>Acuity</th><th>Risk Tier</th><th>Trend</th><th>Flags</th></tr>
              </thead>
              <tbody>
                {data.costDrivers.map(r => (
                  <tr key={r.specialty}>
                    <td className="fw-semibold">{r.specialty}</td>
                    <td>{fmtDollar(r.pmpm)}</td>
                    <td className="text-danger fw-semibold">{r.vs}</td>
                    <td>{r.price}</td><td>{r.volume}</td><td>{r.acuity}</td>
                    <td><TierBadge tier={r.tier} /></td>
                    <td>{r.trend}</td>
                    <td><span className="dash-flag">{r.flags}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function UtilCostView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/util')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Cost Intelligence  ·  Utilization & Cost Detail" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>
      <div className="card">
        <div className="card-header fw-semibold">Utilization & Cost Detail — Place of Service Drill-Down</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr>
                  <th>Category</th><th>Sub-Category</th><th>Metric</th>
                  <th>Prior Period</th><th>Current Period</th><th>Change ($)</th><th>% Change</th>
                </tr>
              </thead>
              <tbody>
                {data.utilRows.map((r, i) => (
                  <tr key={i} style={r.bold ? { background: '#f0f4f8', fontWeight: 600 } : {}}>
                    <td className="fw-semibold" style={{ color: r.section ? 'var(--eh-navy)' : 'transparent', fontSize: '0.8rem', letterSpacing: '0.04em' }}>{r.section || '·'}</td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--eh-text-mid)' }}>{r.sub}</td>
                    <td>{r.metric}</td>
                    <td>{fmtRowVal(r.prior, r.metric)}</td>
                    <td className="fw-semibold">{fmtRowVal(r.current, r.metric)}</td>
                    <td className="fw-semibold">{fmtRowVal(r.current - r.prior, r.metric)}</td>
                    <td className="text-danger fw-semibold">{fmtPct(r.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function CostDriversView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/drivers')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Cost Intelligence  ·  Medical Cost Drivers" memberCount={memberCount} />
      <div className="row g-3">
        {data.costDriverPanels.map(panel => (
          <div key={panel.title} className="col-12 col-md-6 col-xl-3">
            <div className="card h-100">
              <div className="card-header fw-semibold" style={{ fontSize: '0.78rem', letterSpacing: '0.04em' }}>{panel.title}</div>
              <div className="card-body p-2">
                <div className="mb-2">
                  <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--eh-navy)' }}>{panel.headline}</span>
                  <span className="ms-2" style={{ fontSize: '0.78rem', color: '#dc3545' }}>{panel.sub}</span>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: '0.75rem' }}>
                  <thead><tr><th style={{ padding: '2px 4px' }}>Metric</th><th style={{ padding: '2px 4px' }}>Prior</th><th style={{ padding: '2px 4px' }}>Current</th><th style={{ padding: '2px 4px' }}>Trend</th></tr></thead>
                  <tbody>
                    {panel.rows.map(([metric, prior, cur, trend, isNeg]) => (
                      <tr key={metric}>
                        <td style={{ padding: '2px 4px' }}>{metric}</td>
                        <td style={{ padding: '2px 4px', color: '#6c757d' }}>{prior}</td>
                        <td style={{ padding: '2px 4px', fontWeight: 600 }}>{cur}</td>
                        <td style={{ padding: '2px 4px', color: isNeg ? '#dc3545' : '#198754', fontWeight: 600 }}>{trend}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function HeatmapView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/heatmap')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  const allHeatVals = data.heatmapRows.flatMap(r => r.vals);
  const heatMin = Math.min(...allHeatVals);
  const heatMax = Math.max(...allHeatVals);

  return (
    <>
      <PageHeader title="Cost Intelligence  ·  Specialty Opportunity Heatmap & Benchmarking" memberCount={memberCount} />
      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <div className="card">
            <div className="card-header fw-semibold">Specialty Opportunity Heatmap — Spend Deviation vs. Peer Benchmark ($K)</div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 dash-table">
                  <thead><tr><th>Specialty</th>{data.heatmapMarkets.map(m => <th key={m} className="text-center">{m}</th>)}</tr></thead>
                  <tbody>
                    {data.heatmapRows.map(r => (
                      <tr key={r.specialty}>
                        <td className="fw-semibold">{r.specialty}</td>
                        {r.vals.map((v, i) => {
                          const { bg, color } = heatColor(v, heatMin, heatMax);
                          return (
                            <td key={i} className="text-center fw-semibold" style={{ background: bg, color }}>{fmtDollarK(v)}</td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>
                <span>Low</span>
                <div style={{ flex: 1, height: '10px', borderRadius: '4px', background: 'linear-gradient(to right, #cfe2ff, #003c71)' }} />
                <span>High opportunity</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="card h-100">
            <div className="card-header fw-semibold">Specialty Benchmarking — Cost O/E vs. Peer Group</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.benchmarkOe} margin={{ top: 20, right: 15, left: 5, bottom: 55 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, angle: -35, textAnchor: 'end' }} interval={0} />
                  <YAxis type="number" domain={[0.8, 1.4]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}×`} />
                  <RechartTooltip formatter={v => [`${v}×`, 'O/E Ratio']} />
                  <Bar dataKey="oe" radius={[3, 3, 0, 0]}>
                    {data.benchmarkOe.map((e, i) => <Cell key={i} fill={OE_COLORS[i]} />)}
                    <LabelList dataKey="oe" position="top" formatter={v => `${v}×`} style={{ fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-header fw-semibold">Executive Summary — Spend Concentration</div>
        <div className="card-body">
          <p className="mb-3" style={{ fontSize: '0.85rem' }}>
            Top 3 specialties account for{' '}
            <strong style={{ color: 'var(--eh-teal)', fontSize: '1.1rem' }}>{data.top3Pct}%</strong>{' '}
            of total specialty spend.
          </p>
          <div className="d-flex rounded overflow-hidden" style={{ height: '48px' }}>
            {data.spendConcentration.map(s => (
              <div key={s.label} title={`${s.label}: ${s.pct}%`} style={{
                flex: s.pct,
                background: s.c,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: s.pct > 20 ? '#fff' : '#003c71',
                fontSize: '0.72rem', fontWeight: 700, overflow: 'hidden',
              }}>
                {s.pct}%
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2" style={{ fontSize: '0.72rem' }}>
            {data.spendConcentration.map(s => (
              <span key={s.label}><span style={{ color: s.c, fontWeight: 700 }}>■</span> {s.label} ({s.pct}%)</span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Top Provider / Facility Drill-Down shared helpers ─────────────────────────

function varClass(v) {
  if (!v || typeof v !== 'string') return '';
  return v.startsWith('+') ? 'text-danger' : 'text-success';
}

const PROVIDER_METRICS = ['Attribution', 'Uniq Svc Mbrs', 'Visits/1K', 'Svcs/1K', 'Cost/Visit', 'Cost/Svc', 'PMPM'];
const FACILITY_METRICS = ['Attribution', 'Uniq Svc Mbrs', 'Admits/1K', 'Days/1K', 'Visits/1K', 'Cost/Admit', 'Cost/Day', 'Cost/Visit', 'IP PMPM', 'OP PMPM'];

function CurrVarHeaders({ metrics }) {
  return (
    <thead>
      <tr>
        <th rowSpan={2} style={{ verticalAlign: 'middle' }}>#</th>
        <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Name</th>
        {metrics.map(m => (
          <th key={m} colSpan={2} className="text-center border-start">{m}</th>
        ))}
      </tr>
      <tr>
        {metrics.map(m => (
          <React.Fragment key={m}>
            <th className="text-end border-start" style={{ fontWeight: 400, fontSize: '0.68rem' }}>Curr</th>
            <th className="text-end" style={{ fontWeight: 400, fontSize: '0.68rem' }}>Var%</th>
          </React.Fragment>
        ))}
      </tr>
    </thead>
  );
}

function ProviderRow({ r }) {
  return (
    <tr>
      <td>{r.rank}</td>
      <td className="fw-semibold">▶  {r.name}</td>
      <td className="text-end border-start">{fmtCount(r.attr)}</td>       <td className={varClass(r.attrVar)}>{r.attrVar}</td>
      <td className="text-end border-start">{fmtCount(r.uniqSvcMbrs)}</td> <td className={varClass(r.uniqSvcMbrsVar)}>{r.uniqSvcMbrsVar}</td>
      <td className="text-end border-start">{r.visits1k}</td>              <td className={varClass(r.visits1kVar)}>{r.visits1kVar}</td>
      <td className="text-end border-start">{r.svcs1k}</td>               <td className={varClass(r.svcs1kVar)}>{r.svcs1kVar}</td>
      <td className="text-end border-start">{fmtDollar(r.costVisit)}</td>  <td className={varClass(r.costVisitVar)}>{r.costVisitVar}</td>
      <td className="text-end border-start">{fmtDollar(r.costSvc)}</td>    <td className={varClass(r.costSvcVar)}>{r.costSvcVar}</td>
      <td className="text-end border-start">{fmtDollar(r.pmpm)}</td>       <td className={varClass(r.pmpmVar)}>{r.pmpmVar}</td>
    </tr>
  );
}

function FacilityRow({ r }) {
  return (
    <tr>
      <td>{r.rank}</td>
      <td className="fw-semibold">▶  {r.name}</td>
      <td className="text-end border-start">{fmtCount(r.attr)}</td>        <td className={varClass(r.attrVar)}>{r.attrVar}</td>
      <td className="text-end border-start">{fmtCount(r.uniqSvcMbrs)}</td> <td className={varClass(r.uniqSvcMbrsVar)}>{r.uniqSvcMbrsVar}</td>
      <td className="text-end border-start">{r.admits1k}</td>              <td className={varClass(r.admits1kVar)}>{r.admits1kVar}</td>
      <td className="text-end border-start">{r.days1k}</td>                <td className={varClass(r.days1kVar)}>{r.days1kVar}</td>
      <td className="text-end border-start">{r.visits1k}</td>              <td className={varClass(r.visits1kVar)}>{r.visits1kVar}</td>
      <td className="text-end border-start">{fmtDollar(r.costAdmit)}</td>  <td className={varClass(r.costAdmitVar)}>{r.costAdmitVar}</td>
      <td className="text-end border-start">{fmtDollar(r.costDay)}</td>    <td className={varClass(r.costDayVar)}>{r.costDayVar}</td>
      <td className="text-end border-start">{fmtDollar(r.costVisit)}</td>  <td className={varClass(r.costVisitVar)}>{r.costVisitVar}</td>
      <td className="text-end border-start">{fmtDollar(r.ipPmpm)}</td>     <td className={varClass(r.ipPmpmVar)}>{r.ipPmpmVar}</td>
      <td className="text-end border-start">{fmtDollar(r.opPmpm)}</td>     <td className={varClass(r.opPmpmVar)}>{r.opPmpmVar}</td>
    </tr>
  );
}

function DrillThroughCard({ label, codeTypeLabel, codeTypes, activeCodeType, headerLine, children }) {
  return (
    <div className="card mt-3">
      <div className="card-header d-flex align-items-center flex-wrap gap-2">
        <span className="fw-semibold" style={{ fontSize: '0.8rem' }}>DRILL-THROUGH: {label}  |  Select code type</span>
        <div className="btn-group btn-group-sm ms-auto">
          {codeTypes.map(t => (
            <button key={t} className={`btn btn-outline-primary${t === activeCodeType ? ' active' : ''}`} style={{ fontSize: '0.72rem' }}>{t}</button>
          ))}
        </div>
      </div>
      <div className="card-header" style={{ background: 'var(--eh-bg-page)', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em', color: 'var(--eh-navy)' }}>
        {headerLine}
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          {children}
        </div>
      </div>
      <div className="card-footer" style={{ fontSize: '0.7rem', color: 'var(--eh-text-mid)' }}>
        ▶ Click row to expand  ·  Green = favorable vs prior  ·  Red = unfavorable  ·  Curr = Current Period  ·  Var% = % change vs Prior
      </div>
    </div>
  );
}

function TopProviderDrillDownView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/top-providers')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Cost Intelligence  ·  Top Providers — Drill-Down" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>
      <div className="card mt-3">
        <div className="card-header fw-semibold">TOP PROVIDERS — CURRENT vs. PRIOR PERIOD</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.72rem' }}>
              <CurrVarHeaders metrics={PROVIDER_METRICS} />
              <tbody>
                {data.providers.map(r => <ProviderRow key={r.rank} r={r} />)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <DrillThroughCard
        label={data.drillProvider}
        codeTypes={['CPT Codes', 'HCPCS Codes', 'DRG Codes', 'REV Codes']}
        activeCodeType="CPT Codes"
        headerLine={`CPT DETAIL — ${data.drillProvider.toUpperCase()}  |  Period: Q1 2026 vs Q1 2025`}
      >
        <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.72rem' }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ verticalAlign: 'middle' }}>CPT</th>
              <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Description</th>
              {PROVIDER_METRICS.map(m => (
                <th key={m} colSpan={2} className="text-center border-start">{m}</th>
              ))}
            </tr>
            <tr>
              {PROVIDER_METRICS.map(m => (
                <React.Fragment key={m}>
                  <th className="text-end border-start" style={{ fontWeight: 400, fontSize: '0.68rem' }}>Curr</th>
                  <th className="text-end" style={{ fontWeight: 400, fontSize: '0.68rem' }}>Var%</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.drillCodes.map(r => (
              <tr key={r.code}>
                <td className="fw-semibold">{r.code}</td>
                <td>{r.desc}</td>
                <td className="text-end border-start">{fmtCount(r.attr)}</td>       <td className={varClass(r.attrVar)}>{r.attrVar}</td>
                <td className="text-end border-start">{fmtCount(r.uniqSvcMbrs)}</td> <td className={varClass(r.uniqSvcMbrsVar)}>{r.uniqSvcMbrsVar}</td>
                <td className="text-end border-start">{r.visits1k}</td>              <td className={varClass(r.visits1kVar)}>{r.visits1kVar}</td>
                <td className="text-end border-start">{r.svcs1k}</td>               <td className={varClass(r.svcs1kVar)}>{r.svcs1kVar}</td>
                <td className="text-end border-start">{fmtDollar(r.costVisit)}</td>  <td className={varClass(r.costVisitVar)}>{r.costVisitVar}</td>
                <td className="text-end border-start">{fmtDollar(r.costSvc)}</td>    <td className={varClass(r.costSvcVar)}>{r.costSvcVar}</td>
                <td className="text-end border-start">{fmtDollar(r.pmpm)}</td>       <td className={varClass(r.pmpmVar)}>{r.pmpmVar}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DrillThroughCard>
    </>
  );
}

function TopFacilityDrillDownView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/cost-intelligence/top-facilities')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Cost Intelligence  ·  Top Facilities — Drill-Down" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>
      <div className="card mt-3">
        <div className="card-header fw-semibold">TOP FACILITIES — CURRENT vs. PRIOR PERIOD</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.72rem' }}>
              <CurrVarHeaders metrics={FACILITY_METRICS} />
              <tbody>
                {data.facilities.map(r => <FacilityRow key={r.rank} r={r} />)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <DrillThroughCard
        label={data.drillFacility}
        codeTypes={['CPT Codes', 'HCPCS Codes', 'DRG Codes', 'REV Codes']}
        activeCodeType="DRG Codes"
        headerLine={`DRG DETAIL — ${data.drillFacility.toUpperCase()}  |  Period: Q1 2026 vs Q1 2025`}
      >
        <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.72rem' }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ verticalAlign: 'middle' }}>DRG</th>
              <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Description</th>
              {FACILITY_METRICS.map(m => (
                <th key={m} colSpan={2} className="text-center border-start">{m}</th>
              ))}
            </tr>
            <tr>
              {FACILITY_METRICS.map(m => (
                <React.Fragment key={m}>
                  <th className="text-end border-start" style={{ fontWeight: 400, fontSize: '0.68rem' }}>Curr</th>
                  <th className="text-end" style={{ fontWeight: 400, fontSize: '0.68rem' }}>Var%</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.drillCodes.map(r => (
              <tr key={r.code}>
                <td className="fw-semibold">{r.code}</td>
                <td>{r.desc}</td>
                <td className="text-end border-start">{fmtCount(r.attr)}</td>        <td className={varClass(r.attrVar)}>{r.attrVar}</td>
                <td className="text-end border-start">{fmtCount(r.uniqSvcMbrs)}</td> <td className={varClass(r.uniqSvcMbrsVar)}>{r.uniqSvcMbrsVar}</td>
                <td className="text-end border-start">{r.admits1k}</td>              <td className={varClass(r.admits1kVar)}>{r.admits1kVar}</td>
                <td className="text-end border-start">{r.days1k}</td>                <td className={varClass(r.days1kVar)}>{r.days1kVar}</td>
                <td className="text-end border-start">{r.visits1k}</td>              <td className={varClass(r.visits1kVar)}>{r.visits1kVar}</td>
                <td className="text-end border-start">{fmtDollar(r.costAdmit)}</td>  <td className={varClass(r.costAdmitVar)}>{r.costAdmitVar}</td>
                <td className="text-end border-start">{fmtDollar(r.costDay)}</td>    <td className={varClass(r.costDayVar)}>{r.costDayVar}</td>
                <td className="text-end border-start">{fmtDollar(r.costVisit)}</td>  <td className={varClass(r.costVisitVar)}>{r.costVisitVar}</td>
                <td className="text-end border-start">{fmtDollar(r.ipPmpm)}</td>     <td className={varClass(r.ipPmpmVar)}>{r.ipPmpmVar}</td>
                <td className="text-end border-start">{fmtDollar(r.opPmpm)}</td>     <td className={varClass(r.opPmpmVar)}>{r.opPmpmVar}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DrillThroughCard>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SUBTABS = [
  { key: 'overview',       label: 'Executive Overview' },
  { key: 'util',           label: 'Util & Cost Detail' },
  { key: 'drivers',        label: 'Medical Cost Drivers' },
  { key: 'heatmap',        label: 'Heatmap & Benchmarking' },
  { key: 'top-providers',  label: 'Top Provider Drill Down' },
  { key: 'top-facilities', label: 'Top Facility Drill Down' },
];

const VIEW_COMPONENTS = {
  overview:         OverviewView,
  util:             UtilCostView,
  drivers:          CostDriversView,
  heatmap:          HeatmapView,
  'top-providers':  TopProviderDrillDownView,
  'top-facilities': TopFacilityDrillDownView,
};

export default function CostIntelligence() {
  const [sub, setSub] = useState('overview');
  const [memberCount, setMemberCount] = useState(null);
  const ViewComponent = VIEW_COMPONENTS[sub];
  useEffect(() => {
    apiFetch('/api/dashboards/intelligence-config')
      .then(r => r.json())
      .then(d => setMemberCount(d.membership_count))
      .catch(() => {});
  }, []);
  return (
    <div className="dash-tab-content">
      <SubTabBar tabs={SUBTABS} active={sub} onChange={setSub} />
      <ViewComponent memberCount={memberCount} />
    </div>
  );
}
